/**
 * Renders a scenario preview as an isometric diamond minimap — same shape
 * and palette as the in-game minimap so the picker preview reads as the
 * actual map and not a flat top-down sketch with different colours.
 */

import type { Scenario } from './types';
import { TERRAIN_COLORS } from '../render/terrainColors';

const PLAYER_COLORS = ['#4a90e2', '#d2483a', '#3a9a4f', '#d4a017', '#9b59b6', '#e67e22'];

function hex(n: number): string {
  return '#' + n.toString(16).padStart(6, '0');
}

/**
 * Draw the scenario as an isometric diamond inside the returned canvas.
 * The diamond outline matches the map's actual W×H — rectangular maps
 * draw as elongated rhombi, not padded with empty black space inside a
 * square diamond. Canvas itself stays transparent. Inset shadow + bevel
 * rim give the diamond a recessed-tablet look.
 */
export function renderMinimapCanvas(scenario: Scenario, size = 200): HTMLCanvasElement {
  const { width, height, terrain, buildings } = scenario;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) return canvas;

  // Same isometric projection as TurnGameScene's drawMinimap: tile center
  // (col, row) → (ox + (col - row) * scale * 0.5,  oy + (col + row) * scale * 0.25).
  // We compute scale + (ox, oy) so the four corners of the W×H grid fit
  // inside the canvas with a small inset margin.
  const margin = 6;
  const usable = size - margin * 2;
  // World extent: diagonal-1 across X, diagonal-2 across Y in projected coords
  // = (W + H) tiles wide and (W + H) tiles tall after the 0.25 vertical
  // squish — but we want the *map* corners to fit, not the bounding box, so
  // use width = (W + H - 2) * scale * 0.5 and height = (W + H - 2) * scale * 0.25,
  // plus one tile of padding so edge tiles aren't clipped.
  const projWUnits = (width + height) * 0.5;
  const projHUnits = (width + height) * 0.25;
  const scale = Math.min(usable / projWUnits, usable / projHUnits);
  const halfTile = scale * 0.5;
  const quarter = scale * 0.25;

  // Tile center as a function of (col, row); ox/oy adjusted after we know
  // the bounding box of the four map corners (top-N, right-E, bottom-S,
  // left-W) so the whole thing sits centred on the canvas.
  const projectCol = (col: number, row: number, ox: number, oy: number): { x: number; y: number } => ({
    x: ox + (col - row) * halfTile,
    y: oy + (col + row) * quarter,
  });

  // Start with the canvas centre, measure where the corners land, then shift.
  let ox = size / 2;
  let oy = size / 2;
  const corners = [
    projectCol(0, 0, ox, oy),                  // top
    projectCol(width - 1, 0, ox, oy),          // right
    projectCol(width - 1, height - 1, ox, oy), // bottom
    projectCol(0, height - 1, ox, oy),         // left
  ];
  // Each corner has a tile-sized halo (the tile extends from center).
  const minX = Math.min(...corners.map((c) => c.x)) - halfTile;
  const maxX = Math.max(...corners.map((c) => c.x)) + halfTile;
  const minY = Math.min(...corners.map((c) => c.y)) - quarter;
  const maxY = Math.max(...corners.map((c) => c.y)) + quarter;
  const dx = size / 2 - (minX + maxX) / 2;
  const dy = size / 2 - (minY + maxY) / 2;
  ox += dx;
  oy += dy;

  // The 4 outer corners of the actual map diamond (not the W+H bounding
  // square). Used both as clip path and as the visible recessed shape.
  const p = [
    projectCol(0, 0, ox, oy),
    projectCol(width - 1, 0, ox, oy),
    projectCol(width - 1, height - 1, ox, oy),
    projectCol(0, height - 1, ox, oy),
  ];
  // Extend outwards by half-a-tile so the path encloses the outermost tile
  // pixels too (otherwise the clip would cut their slope corners).
  const extend = (a: { x: number; y: number }, sign: number): { x: number; y: number } => ({
    x: a.x,
    y: a.y + sign * quarter,
  });
  const mapPolygon: { x: number; y: number }[] = [
    extend(p[0], -1), // top tip — push up
    { x: p[1].x + halfTile, y: p[1].y },  // right tip — push right
    extend(p[2], +1), // bottom tip — push down
    { x: p[3].x - halfTile, y: p[3].y },  // left tip — push left
  ];

  const buildPolygonPath = (): void => {
    ctx.beginPath();
    ctx.moveTo(mapPolygon[0].x, mapPolygon[0].y);
    for (let i = 1; i < mapPolygon.length; i++) ctx.lineTo(mapPolygon[i].x, mapPolygon[i].y);
    ctx.closePath();
  };

  // 1. Filled background limited to the map's actual diamond shape, then
  //    clip subsequent draws to it. No filler outside the map.
  ctx.save();
  buildPolygonPath();
  ctx.fillStyle = '#0a0d12';
  ctx.fill();
  ctx.clip();

  // 2. Terrain tiles
  for (let row = 0; row < height; row++) {
    for (let col = 0; col < width; col++) {
      const t = terrain[row][col];
      ctx.fillStyle = hex(TERRAIN_COLORS[t]);
      const { x, y } = projectCol(col, row, ox, oy);
      ctx.fillRect(x - halfTile, y - quarter, scale, scale * 0.5);
    }
  }

  // 3. Building markers — small bright dots
  for (const b of buildings) {
    const { x, y } = projectCol(b.col, b.row, ox, oy);
    ctx.fillStyle = b.owner != null ? PLAYER_COLORS[b.owner % PLAYER_COLORS.length] : '#ffffff';
    ctx.fillRect(x - 1.5, y - 1.5, 3, 3);
  }
  ctx.restore();

  // 4. Inset shadow — fill the canvas minus the polygon with a soft drop
  //    shadow (even-odd). Only the shadow stays visible inside the polygon
  //    edge → recessed tablet effect.
  ctx.save();
  buildPolygonPath();
  ctx.clip();
  ctx.beginPath();
  ctx.moveTo(-size, -size);
  ctx.lineTo(size * 2, -size);
  ctx.lineTo(size * 2, size * 2);
  ctx.lineTo(-size, size * 2);
  ctx.closePath();
  ctx.moveTo(mapPolygon[0].x, mapPolygon[0].y);
  for (let i = mapPolygon.length - 1; i >= 1; i--) ctx.lineTo(mapPolygon[i].x, mapPolygon[i].y);
  ctx.closePath();
  ctx.shadowColor = 'rgba(0, 0, 0, 0.85)';
  ctx.shadowBlur = 12;
  ctx.shadowOffsetX = 0;
  ctx.shadowOffsetY = 4;
  ctx.fillStyle = 'rgba(0,0,0,0)';
  ctx.fill('evenodd');
  ctx.shadowColor = 'rgba(0, 0, 0, 0.55)';
  ctx.shadowBlur = 6;
  ctx.shadowOffsetY = 2;
  ctx.fill('evenodd');
  ctx.restore();

  // 5. Bevel highlights — light along the upper rim (NW + NE), shadow along
  //    the lower rim. Softens the stepped tile edges.
  ctx.save();
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.moveTo(mapPolygon[3].x, mapPolygon[3].y);
  ctx.lineTo(mapPolygon[0].x, mapPolygon[0].y);
  ctx.lineTo(mapPolygon[1].x, mapPolygon[1].y);
  ctx.strokeStyle = 'rgba(180, 200, 220, 0.22)';
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(mapPolygon[1].x, mapPolygon[1].y);
  ctx.lineTo(mapPolygon[2].x, mapPolygon[2].y);
  ctx.lineTo(mapPolygon[3].x, mapPolygon[3].y);
  ctx.strokeStyle = 'rgba(0, 0, 0, 0.55)';
  ctx.stroke();
  ctx.restore();

  return canvas;
}
